import React, { FC, useMemo } from 'react';
import { getCustomDRTSummaryColumns } from '../../../utils/data';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import Conditional from '../../common/conditional';
import CustomSection from '../custom-general/custom-section';
import CustomSummary from '../custom-general/custom-summary';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import DRTDataPage from './drt-data-page';
import IsometricDrawing from '../custom-utm/isometric-drawing';
import ExecutiveSummary from '../custom-visual-inspection/executive-summary';
import GeneralInformation from './generalInformation';
import OverviewSheet from '../custom-general/overview-sheet';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface DRTReportProps {
	certificates?: ICertificate;
	reportTitle: string;
	executiveSummary: string;
	projectDetails: models.Project;
	drtReport: models.CustomDRTReportItem[];
	coverData: models.ExxonMobilFVIPipingCoverData;
	summaryItems: models.CustomDRTItem[];
	photoRecord: models.PDFImageProps[];
	drawing: models.PDFImageProps[];
}

const DRTReport: FC<DRTReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure ?? '',
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			<ExecutiveSummary summary={props.executiveSummary} />

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			<GeneralInformation reportItems={props.drtReport} />

			<CustomSummary
				columns={getCustomDRTSummaryColumns()}
				data={props.drtReport}
				summary={props.executiveSummary}
			/>

			<IsometricDrawing 
				isoImages={props.drawing} 
				label="Drawing / Iso"  
			/>

			{props.drtReport.map((reportItem) => (
				<CustomSection key={reportItem.shotNumber} sectionTitle="Digital Radiography Report">
					<DRTDataPage {...reportItem} />
				</CustomSection>
			))}

			<Conditional isVisible={props.photoRecord.length > 0}>
				<CustomGeneralPhotos label="Photographic Record" photos={props.photoRecord} />
			</Conditional>
		</React.Fragment>
	);
};

export default withPDF(DRTReport);
